import { ServerCart } from 'components/molecules/Goods/utils';
import {
  addMadinaCartItem,
  clearMadinaCart,
  getCachedMadinaProductsAllBranches,
  getMadinaCart,
  getMadinaBranches,
  getMadinaCategories,
  getMadinaProductDetail,
  getMadinaProducts,
  revalidateMadinaCart,
  searchMadinaProductsAllBranches
} from 'network/services/madina';
import { getStoredBranchId } from 'utils/branch';
import {
  madinaProductMatchesSubcategoryLabel,
  normalizeCatalogLabel,
  resolveMadinaSubcategoryId
} from 'utils/madinaCatalog';
import { mapMadinaCartToServerCart, mapMadinaCategoryToLegacy, mapMadinaProductToLegacy } from 'utils/madinaMappers';
import {
  BUSINESS_DELETE_ORDER,
  BUSINESS_VIEW_CART,
  CLEAR_CART,
  DELETE_ORDER,
  GET_ALL_CATEGORIES,
  GET_ALL_PRODUCT,
  GET_FAVOURITE_PRODUCTS,
  GET_PRODUCT_DETAIL,
  GET_PRODUCTS,
  GET_PRODUCTS_BY_SEARCH,
  GET_PRODUCTS_BY_SUBCATEGORY,
  GET_PRODUCTS_BY_VENDOR,
  GET_PRODUCTS_BY_VENDOR_AND_CATEGORY,
  GET_PRODUCTS_BY_VENDORS_CATEGORIES,
  GET_SUB_CATEGORIES,
  GET_VENDOR_BY_ID_FRAGMENT,
  GET_VENDORS_BY_CATEGORIES,
  VENDOR,
  VIEW_CART
} from 'network/config/endpoints';
import api from 'network/config/setAuthHeader';
import type {
  Category,
  CustomerType,
  FavouriteProductsResponse,
  Product,
  ResponseSuccess,
  Vendor
} from 'TYPES/api.body';
import type { MadinaCategory, MadinaProduct } from 'TYPES/madina';

const paginateMadinaCatalog = (products: MadinaProduct[], page = 0, size = 40): Product[] => {
  const start = page * size;
  return products.slice(start, start + size).map(mapMadinaProductToLegacy);
};

const madinaCatalogCacheKey = (prefix: string, parts: Record<string, string | number | undefined>) =>
  `${prefix}:${Object.entries(parts)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}=${value}`)
    .join('&')}`;

const resolveCatalogBranchId = (locationId?: string | number): number | null => {
  if (locationId != null && String(locationId).trim() !== '') {
    const fromUrl = Number(locationId);
    if (Number.isFinite(fromUrl) && fromUrl > 0) return fromUrl;
  }
  return getStoredBranchId();
};

const subCategoryNamesFromProducts = (products: MadinaProduct[]): string[] => {
  const labels = new Set<string>();
  products.forEach(product => {
    const name = product.subcategory?.name?.trim();
    if (name) labels.add(name);
  });
  return Array.from(labels);
};

const fetchLegacyVendorCategoryProducts = async (opts: ProductsByVendorsOpts): Promise<any | null> => {
  const { vendorId, categoryId, locationId, page = 0, size = 100 } = opts;
  const params = new URLSearchParams({
    vendorId: vendorId?.toString() || '',
    categoryId: categoryId?.toString() || '',
    page: page.toString(),
    size: size.toString()
  });
  if (locationId) {
    params.set('locationId', locationId.toString());
  }
  const { data } = await api.get(`${GET_PRODUCTS_BY_VENDORS_CATEGORIES}/?${params.toString()}`);
  return data ?? null;
};

const catalogProductForFavourite = async (productId: number) => {
  const branchIds: number[] = [];
  const stored = getStoredBranchId();
  if (stored) branchIds.push(stored);
  try {
    const branches = await getMadinaBranches();
    branches.forEach(branch => {
      if (!branchIds.includes(branch.id)) branchIds.push(branch.id);
    });
  } catch {
    // The stored branch is enough when the branch list is unavailable.
  }

  for (const branchId of branchIds) {
    try {
      const product = await getMadinaProductDetail(productId, branchId);
      const price = product.min_price ?? product.price ?? 0;
      if (price > 0 || product.description) return product;
    } catch {
      // Try the next store.
    }
  }
  return null;
};

export const getFavouriteProducts = async (): Promise<FavouriteProductsResponse> => {
  const { data } = await api.get(GET_FAVOURITE_PRODUCTS);
  const rows: Product[] = Array.isArray(data?.data) ? data.data : [];
  if (rows.length === 0) return data;

  const hydrated = await Promise.all(
    rows.map(async (item: Product) => {
      const missingPrice = !item.price || item.price <= 0;
      const missingDescription = !String(item.description ?? '').trim();
      if (!missingPrice && !missingDescription) return item;

      const catalog = await catalogProductForFavourite(item.productId);
      if (!catalog) return item;
      const legacy = mapMadinaProductToLegacy(catalog);
      return {
        ...item,
        productName: item.productName || legacy.productName,
        price: missingPrice ? legacy.price : item.price,
        bulkOrderPrice: item.bulkOrderPrice || legacy.bulkOrderPrice,
        description: missingDescription ? legacy.description : item.description,
        quantityAvailable: item.quantityAvailable ?? legacy.quantityAvailable,
        status: item.status || legacy.status,
        images: Array.isArray(item.images) && item.images.length > 0 ? item.images : legacy.images,
        seller: item.seller?.businessName
          ? { ...legacy.seller, ...item.seller, seller_logo: item.seller.seller_logo || legacy.seller?.seller_logo }
          : legacy.seller
      };
    })
  );

  return { ...data, data: hydrated };
};

export const getAllProducts = async (args: { page: number; limit: number }): Promise<Product[]> => {
  try {
    const merged = await getCachedMadinaProductsAllBranches('catalog:all', {});
    if (merged.length > 0) {
      return paginateMadinaCatalog(merged, args.page, args.limit);
    }
  } catch {
    // legacy fallback
  }

  const { data } = await api.get(`${GET_ALL_PRODUCT}?page=${args.page}&size=${args.limit}`);
  return data.data;
};

interface ProductByCategoryOpts {
  categoryId: string | number;
  page?: number;
  size?: number;
}

export const getProductByCategory = async (opts?: ProductByCategoryOpts): Promise<Product[]> => {
  if (opts?.categoryId) {
    try {
      const cacheKey = madinaCatalogCacheKey('category', { categoryId: Number(opts.categoryId) });
      const merged = await getCachedMadinaProductsAllBranches(cacheKey, {
        category_id: Number(opts.categoryId)
      });
      if (merged.length > 0) {
        return paginateMadinaCatalog(merged, opts?.page ?? 0, opts?.size ?? 40);
      }
    } catch {
      // legacy fallback
    }
  }

  const { data } = await api.get(
    `${GET_PRODUCTS}category?categoryId=${opts?.categoryId}&page=${opts?.page ?? 0}&size=${opts?.size ?? 40}`
  );

  return data.data;
};

export const getAllCategories = async (vendorId?: number) => {
  try {
    const categories = await getMadinaCategories();
    if (categories.length > 0) {
      return categories.map(mapMadinaCategoryToLegacy);
    }
  } catch {
    // legacy fallback
  }
  const data = await allCategories();
  return data.data;
};

interface VendorsByCategoryOpts {
  categoryId: string | number;
  page?: number;
  size?: number;
}
export const getVendorsByCategory = async (opts?: VendorsByCategoryOpts): Promise<any> => {
  const { data } = await api.get(
    `${GET_VENDORS_BY_CATEGORIES}/${opts?.categoryId}?page=${opts?.page ?? 0}&perPage=${opts?.size ?? 50}`
  );

  return data.data;
};

interface ProductsByVendorsOpts {
  categoryId: string | number;
  vendorId: string | number;
  page?: number;
  size?: number;
}

interface ProductsByVendorsOpts {
  categoryId: string | number;
  vendorId: string | number;
  locationId?: string | number; // ✅ Add locationId
  page?: number;
  size?: number;
}

export const getProductsByVendorsandCategories = async (opts?: ProductsByVendorsOpts): Promise<any> => {
  const { vendorId, categoryId, locationId, page = 0, size = 100 } = opts || {};
  const branchId = resolveCatalogBranchId(locationId);

  const buildMadinaVendorPage = (
    products: MadinaProduct[],
    meta: Record<string, unknown> | undefined,
    pageIndex: number,
    pageSize: number
  ) => {
    const pagination = meta?.pagination as { total_pages?: number } | undefined;
    const totalPages =
      pagination?.total_pages ??
      (products.length >= pageSize ? Math.max(pageIndex + 2, 2) : Math.max(pageIndex + 1, 1));
    const first = products[0];

    return {
      data: products.map(mapMadinaProductToLegacy),
      currentPage: pageIndex,
      totalPages: Math.max(1, totalPages),
      seller: first?.vendor
        ? {
            id: first.vendor?.id,
            businessName: first.vendor?.name ?? first.vendor?.business_name,
            seller_logo: first.vendor?.logo
          }
        : null,
      ...(pageIndex === 0 ? { subCategories: subCategoryNamesFromProducts(products) } : {})
    };
  };

  if (vendorId && categoryId && branchId) {
    try {
      const { products, meta } = await getMadinaProducts({
        branch_id: branchId,
        category_id: Number(categoryId),
        vendor_id: Number(vendorId),
        page: page + 1,
        per_page: size
      });
      return buildMadinaVendorPage(products, meta, page, size);
    } catch {
      try {
        const legacy = await fetchLegacyVendorCategoryProducts({
          vendorId,
          categoryId,
          locationId,
          page,
          size
        });
        if (legacy) return legacy;
      } catch {
        // empty response below
      }

      return buildMadinaVendorPage([], undefined, page, size);
    }
  }

  if (vendorId && categoryId) {
    try {
      const legacy = await fetchLegacyVendorCategoryProducts({
        vendorId,
        categoryId,
        locationId,
        page,
        size
      });
      if (legacy?.data?.length) return legacy;
    } catch {
      // try Madina all-branch as last resort (no branch in URL)
    }

    try {
      const cacheKey = madinaCatalogCacheKey('vendor-category', {
        vendorId: Number(vendorId),
        categoryId: Number(categoryId),
        locationId: 'all'
      });
      const merged = await getCachedMadinaProductsAllBranches(
        cacheKey,
        {
          vendor_id: Number(vendorId),
          category_id: Number(categoryId)
        },
        { maxPagesPerBranch: 5 }
      );

      const vendorFiltered = merged.filter(product => {
        const productVendorId = product.vendor?.id ?? product.vendor_id;
        return !Number(vendorId) || !productVendorId || productVendorId === Number(vendorId);
      });

      if (vendorFiltered.length > 0) {
        const mapped = paginateMadinaCatalog(vendorFiltered, page, size);
        return {
          data: mapped,
          currentPage: page,
          totalPages: Math.max(1, Math.ceil(vendorFiltered.length / size)),
          seller: vendorFiltered[0]?.vendor
            ? {
                id: vendorFiltered[0].vendor?.id,
                businessName: vendorFiltered[0].vendor?.name,
                seller_logo: vendorFiltered[0].vendor?.logo
              }
            : null,
          subCategories: subCategoryNamesFromProducts(vendorFiltered)
        };
      }
    } catch {
      // legacy fallback
    }
  }

  return fetchLegacyVendorCategoryProducts({
    vendorId: vendorId!,
    categoryId: categoryId!,
    locationId,
    page,
    size
  });
};

export const getSubcategories = async ({ queryKey }: any) => {
  const [, { categoryId }] = queryKey;

  if (categoryId) {
    try {
      const categories = await getMadinaCategories();
      const category = categories.find(item => item.id === Number(categoryId));
      if (category) {
        return (category.subcategories ?? []).map((sub: MadinaCategory) => ({
          id: sub.id,
          name: sub.name,
          logo: sub.image_url ?? sub.logo ?? ''
        }));
      }
    } catch {
      // legacy fallback
    }
  }

  const { data } = await api.get(`${GET_SUB_CATEGORIES}?categoryId=${categoryId}`);
  return data.data;
};

export interface ProductsByCategoryOpts {
  subCategoryId: number;
  page?: number;
  size?: number;
  vendorId?: number;
}

export const getProductsBySubCategory = async (opts: ProductsByCategoryOpts): Promise<ResponseSuccess<Product[]>> => {
  const { data } = await api.get(
    `${GET_PRODUCTS_BY_SUBCATEGORY}?subCategoryId=${opts.subCategoryId}&page=${opts.page ?? 0}&size=${opts.size ?? 6}`
  );
  return data;
};

export interface ProductsBySearchOpts {
  search: string;
  page?: number;
  size?: number;
}

export const getProductsBySearch = async (opts: ProductsBySearchOpts): Promise<ResponseSuccess<Product[]>> => {
  if ((opts.search?.length ?? 0) >= 2) {
    try {
      const merged = await searchMadinaProductsAllBranches(opts.search, { limit: opts.size ?? 40 });
      const page = opts.page ?? 0;
      const size = opts.size ?? 40;
      const start = page * size;
      const slice = merged.slice(start, start + size).map(mapMadinaProductToLegacy);
      return { data: slice, status: 'success' } as ResponseSuccess<Product[]>;
    } catch {
      // legacy fallback
    }
  }

  const { data } = await api.post(
    GET_PRODUCTS_BY_SEARCH,
    {
      searchQuery: opts.search,
      page: opts.page ?? 0,
      pageSize: opts.size ?? 40
    }
  );
  return data;
};
// export const getProductsBySearch = async (opts: ProductsBySearchOpts): Promise<ResponseSuccess<Product[]>> => {
//   const { data } = await api.get(
//     `${GET_PRODUCTS_BY_SEARCH}?searchQuery=${opts.search}&page=${opts.page ?? 0}&pageSize=${opts.size ?? 6}`
//   );
//   return data;
// };

const allCategories = async (): Promise<ResponseSuccess<Category[]>> => {
  const { data } = await api.get(GET_ALL_CATEGORIES);
  return data;
};

export const getVendorById = async (vendorId: number): Promise<ResponseSuccess<Vendor>> => {
  const { data } = await api.get(`${VENDOR}${vendorId}${GET_VENDOR_BY_ID_FRAGMENT}`);

  return data;
};

export interface ProductsByVendorOpts {
  vendorId: number;
  categoryId?: number;
  page?: number;
  size?: number;
}

export const getProductsByVendor = async (opts: ProductsByVendorOpts): Promise<ResponseSuccess<Product[]>> => {
  let url = '';
  if (opts.categoryId !== undefined) {
    url = `${GET_PRODUCTS_BY_VENDOR_AND_CATEGORY}?vendorId=${opts.vendorId}&categoryId=${opts.categoryId}`;
  } else {
    url = `${GET_PRODUCTS_BY_VENDOR}?vendorId=${opts.vendorId}`;
  }
  const { data } = await api.get(`${url}&page=${opts.page ?? 0}&pageSize=${opts.size ?? 6}`);

  return data;
};

export const viewCart = async (customerType?: CustomerType): Promise<ServerCart> => {
  if (customerType === 'BUSINESS') {
    const { data } = await api.get(BUSINESS_VIEW_CART);
    return data;
  }

  const branchId = getStoredBranchId();
  if (branchId) {
    const cart = await getMadinaCart(branchId);
    return mapMadinaCartToServerCart(cart);
  }

  const { data } = await api.get(VIEW_CART);
  return data;
};

export const clearCart = async () => {
  const branchId = getStoredBranchId();
  if (branchId) {
    const cart = await clearMadinaCart(branchId);
    return mapMadinaCartToServerCart(cart);
  }

  const { data } = await api.delete(CLEAR_CART);
  return data;
};

export const deleteOrder = async (customerType: string, orderId: number, locationId: number = 1) => {
  if (customerType === 'BUSINESS') {
    const { data } = await api.delete(`${BUSINESS_DELETE_ORDER}/${orderId}/${locationId}`);
    return data;
  }

  const branchId = getStoredBranchId();
  if (branchId) {
    const cart = await clearMadinaCart(branchId);
    return mapMadinaCartToServerCart(cart);
  }

  const { data } = await api.delete(`${DELETE_ORDER}/${orderId}/${locationId}`);
  return data;
};

export const getProductDetail = async (id: number): Promise<ResponseSuccess<Product | undefined>> => {
  const branchId = getStoredBranchId();
  if (branchId) {
    const product = await getMadinaProductDetail(id, branchId);
    return { data: mapMadinaProductToLegacy(product), status: 'success' } as ResponseSuccess<Product | undefined>;
  }
  const { data } = await api.get(`${GET_PRODUCT_DETAIL}?productId=${id}`);
  return data;
};

export interface ProductsBySellerSubcategoryNameOpts {
  sellerId: string | number;
  subCategoryName: string;
  page?: number;
  size?: number;
}

export const getProductsBySellerSubcategoryNameAlt = async (
  opts: ProductsBySellerSubcategoryNameOpts & { parentCategoryId?: number; branchId?: number }
): Promise<any> => {
  const { sellerId, subCategoryName, page = 0, size = 40, parentCategoryId, branchId: branchIdOpt } = opts;
  const branchId = branchIdOpt ?? getStoredBranchId() ?? undefined;
  const catalogScope = branchId ? { branchIds: [branchId] } : undefined;
  const decodedName = decodeURIComponent(subCategoryName);
  const numericSubcategoryId = Number(decodedName);

  try {
    const subcategoryId = Number.isFinite(numericSubcategoryId)
      ? numericSubcategoryId
      : await resolveMadinaSubcategoryId(decodedName, parentCategoryId);

    if (branchId) {
      const { products, meta } = await getMadinaProducts({
        branch_id: branchId,
        category_id: parentCategoryId ? Number(parentCategoryId) : undefined,
        subcategory_id: subcategoryId,
        vendor_id: Number(sellerId) || undefined,
        page: page + 1,
        per_page: size
      });

      let pageProducts = products;
      if (subcategoryId) {
        pageProducts = products.filter(
          product =>
            product.subcategory?.id === subcategoryId ||
            madinaProductMatchesSubcategoryLabel(decodedName, product.subcategory?.name)
        );
      } else {
        pageProducts = products.filter(product =>
          madinaProductMatchesSubcategoryLabel(decodedName, product.subcategory?.name)
        );
      }

      const pagination = meta?.pagination as { total_pages?: number } | undefined;
      const totalPages =
        pagination?.total_pages ??
        (pageProducts.length >= size ? Math.max(page + 2, 2) : Math.max(page + 1, 1));

      return {
        data: pageProducts.map(mapMadinaProductToLegacy),
        currentPage: page,
        totalPages: Math.max(1, totalPages)
      };
    }

    const cacheKey = madinaCatalogCacheKey('seller-subcategory', {
      sellerId: Number(sellerId),
      subcategoryId,
      parentCategoryId,
      label: normalizeCatalogLabel(decodedName)
    });

    const filters: Omit<import('TYPES/madina').MadinaProductListParams, 'branch_id' | 'page' | 'per_page'> = {};
    if (subcategoryId) {
      filters.subcategory_id = subcategoryId;
    }
    if (parentCategoryId) {
      filters.category_id = Number(parentCategoryId);
    }
    if (Number(sellerId)) {
      filters.vendor_id = Number(sellerId);
    }

    let merged = await getCachedMadinaProductsAllBranches(cacheKey, filters, catalogScope);

    if (merged.length === 0 && subcategoryId) {
      const broadKey = madinaCatalogCacheKey('seller-subcategory-broad', {
        sellerId: Number(sellerId),
        parentCategoryId,
        branchId
      });
      const broadFilters = { ...filters };
      delete broadFilters.subcategory_id;
      merged = await getCachedMadinaProductsAllBranches(broadKey, broadFilters, catalogScope);
    }

    if (subcategoryId) {
      merged = merged.filter(
        product =>
          product.subcategory?.id === subcategoryId ||
          madinaProductMatchesSubcategoryLabel(decodedName, product.subcategory?.name)
      );
    } else {
      merged = merged.filter(product => madinaProductMatchesSubcategoryLabel(decodedName, product.subcategory?.name));
    }

    if (Number(sellerId)) {
      merged = merged.filter(product => (product.vendor?.id ?? product.vendor_id) === Number(sellerId));
    }

    if (merged.length > 0) {
      const mapped = paginateMadinaCatalog(merged, page, size);
      return {
        data: mapped,
        currentPage: page,
        totalPages: Math.max(1, Math.ceil(merged.length / size))
      };
    }
  } catch {
    // legacy fallback
  }


  const params = new URLSearchParams({
    sellerId: sellerId.toString(),
    subCategoryName: subCategoryName,
    size: size.toString(),
    page: page.toString()
  });

  const { data } = await api.get(`/api/v1/unauthorized/products-by-seller-subcategory?${params.toString()}`);
  return data;
};

export const addTier1CartItem = async (productId: number, quantity: number) => {
  const branchId = getStoredBranchId();
  if (!branchId) throw new Error('Select a branch before adding items to cart');
  return addMadinaCartItem({ branch_id: branchId, product_id: productId, quantity });
};

export const revalidateTier1Cart = async (items?: Array<{ product_id: number; quantity: number }>) => {
  const branchId = getStoredBranchId();
  if (!branchId) throw new Error('Select a branch to revalidate cart');
  return revalidateMadinaCart({ branch_id: branchId, items });
};
